import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Search as SearchIcon, ArrowRight, X } from "lucide-react";
import { search as searchApi } from "@/lib/api";

const TYPE_LABEL = {
  services: "SERVICES",
  industries: "INDUSTRIES",
  insights: "INSIGHTS",
  case_studies: "CASE STUDIES",
  jobs: "OPEN ROLES",
};

const TYPE_TO_PATH = {
  service: "/services/",
  industry: "/industries/",
  insight: "/insights/",
  "case-study": "/case-studies/",
  job: "/careers/",
};

export default function CommandPalette({ open, onClose }) {
  const [q, setQ] = useState("");
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 30);
    } else {
      setQ("");
      setGroups([]);
    }
  }, [open]);

  useEffect(() => {
    if (!q || q.trim().length < 2) {
      setGroups([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const data = await searchApi(q.trim(), 5);
        if (!cancelled) setGroups(data.groups || []);
      } catch {
        if (!cancelled) setGroups([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 220);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [q]);

  useEffect(() => {
    const onKey = (e) => {
      if (!open) return;
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const jump = (item) => {
    const base = TYPE_TO_PATH[item.type];
    if (base && item.slug) {
      navigate(`${base}${item.slug}`);
      onClose();
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[80]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          data-testid="command-palette"
        >
          <div
            className="absolute inset-0 bg-ink/85 backdrop-blur-md"
            onClick={onClose}
            aria-hidden
          />
          <motion.div
            initial={{ y: 24, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 12, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="relative mx-auto mt-24 max-w-2xl px-4"
          >
            <div className="glass-panel-strong rounded-2xl overflow-hidden">
              <div className="flex items-center gap-3 px-5 py-4 border-b border-gold/10">
                <SearchIcon className="h-4 w-4 text-gold" />
                <input
                  ref={inputRef}
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search services, industries, insights, jobs…"
                  className="flex-1 bg-transparent border-0 outline-none text-inkText-primary placeholder-inkText-muted/70 text-sm"
                  data-testid="palette-input"
                />
                <button
                  onClick={onClose}
                  className="text-inkText-muted hover:text-gold"
                  data-testid="palette-close"
                  aria-label="Close search"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="max-h-[60vh] overflow-y-auto p-3">
                {loading && (
                  <div className="p-4 text-xs font-mono tracking-wider text-inkText-muted">
                    SEARCHING…
                  </div>
                )}
                {!loading && q.trim().length >= 2 && groups.length === 0 && (
                  <div className="p-6 text-sm text-inkText-muted">
                    No results for <span className="text-inkText-primary">&ldquo;{q}&rdquo;</span>
                  </div>
                )}
                {!loading &&
                  groups.map((g) => (
                    <div key={g.type} className="mb-4">
                      <div className="px-3 py-1 font-mono text-[10px] tracking-[0.28em] text-gold-body">
                        {TYPE_LABEL[g.type] || g.type.toUpperCase()}
                      </div>
                      <ul>
                        {g.items.map((item) => (
                          <li key={`${g.type}-${item.id}`}>
                            <button
                              onClick={() => jump(item)}
                              data-testid={`palette-result-${g.type}-${item.slug}`}
                              className="w-full text-left flex items-start gap-3 px-3 py-2 rounded-lg hover:bg-ink-elevated/60 transition-colors group"
                            >
                              <span className="mt-1 h-1.5 w-1.5 rounded-full bg-gold flex-shrink-0" />
                              <span className="flex-1">
                                <span className="block text-sm text-inkText-primary">
                                  {item.title}
                                </span>
                                {item.excerpt && (
                                  <span className="block text-xs text-inkText-muted line-clamp-1">
                                    {item.excerpt}
                                  </span>
                                )}
                              </span>
                              <ArrowRight className="h-3.5 w-3.5 text-inkText-muted group-hover:text-gold" />
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                {q.trim().length < 2 && (
                  <div className="p-4 text-xs text-inkText-muted">
                  Type at least 2 characters. Press <kbd className="mx-1 rounded border border-gold/20 px-1 text-[10px]">Esc</kbd> to close.
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
